import React from 'react';
import SeoHead from '../components/SeoHead';
import { getOptimizedImageUrl } from '../lib/cms';

export default function HealingmassagePage() {
  return (
    <>
      <SeoHead
        title="Healingmassage – Niels Boye | Ibjerget på Midtfyn"
        description="Healingmassage v. Niels Boye på Ibjerget. En kærlighedserklæring til kroppen med nærværende berøring, ro og aktivering af dit lyslegeme."
        canonicalUrl="https://www.ibjerget.dk/healingmassage"
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 bg-[#381d6e] text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/ibjerget-kropsterapi-session.jpg', { width: 1400, quality: 80, fit: 'cover' })}
            alt="Healingmassage session på Ibjerget"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#381d6e] via-[#381d6e]/75 to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="fa-solid fa-heart" aria-hidden="true"></i>
            <span>Terapeut Niels Boye</span>
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#faf5f9] tracking-tight leading-tight">
            Healingmassage
          </h1>
          <p className="mt-4 font-serif italic text-lg sm:text-2xl text-[#e895c8]">
            En kærlighedserklæring til kroppen
          </p>
          <p className="mt-4 text-base sm:text-lg text-[#f7eefa] max-w-2xl mx-auto leading-relaxed font-normal">
            En stund, hvor du ikke skal præstere, forklare eller være noget bestemt. Blot tage imod og mærke kroppen i dyb ro.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="#booking"
              className="px-8 py-4 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow-md transition-colors"
            >
              Book tid hos Niels
            </a>
            <a
              href="tel:66157902"
              className="px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone mr-2 text-[#f7eefa]"></i>
              Ring 66 15 79 02
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 sm:py-24 bg-[#faf5f9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Text */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#d9238c] block">
                Nærvær & Omsorg
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1f142e] tracking-tight">
                Et møde med kroppen
              </h2>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Healingmassage er en invitation til at møde dig selv.
                </p>
                <p>
                  En stund, hvor du ikke skal præstere, forklare eller være noget bestemt. Du skal blot tage imod og mærke, hvad der sker, når kroppen bliver mødt med nærvær, ro og opmærksomhed.
                </p>
                <p>
                  Jeg arbejder intuitivt og med stor opmærksomhed på det, jeg møder undervejs. Hvis et område kalder på mere opmærksomhed, bliver jeg dér lidt længere. Samtidig bestræber jeg mig på at lade berøringen komme hele kroppen igennem – fra hoved til fødder, og helt ud i fingre og tæer.
                </p>
                <p>
                  Der ligger noget særligt i en nærværende berøring af de områder, som så let bliver overset. Når f.eks. fingre og tæer får lov til at blive mærket og berørt med ro og opmærksomhed, kan det give en overraskende oplevelse af omsorg og velvære.
                </p>
                <p>
                  Nærværet er en væsentlig del af min måde at arbejde på. Jeg (Niels) forsøger ikke at presse kroppen til at give slip. Jeg lytter til den og følger dens signaler. Nogle gange er en blid berøring det rigtige. Andre gange kalder kroppen på et dybere eller længerevarende arbejde.
                </p>
                <p>
                  Måske har du gennem mange år båret på spændinger, vaner og erfaringer, som har sat sig i kroppen. Healingmassagen giver dig mulighed for at sænke tempoet og mærke kroppen på en anden måde – med dyb respekt og uden krav.
                </p>
              </div>

              {/* Callout quote box */}
              <div className="bg-[#faf5f9] p-6 sm:p-8 rounded-2xl border-l-4 border-[#d9238c] text-[#1f142e] space-y-2">
                <p className="font-serif italic text-lg text-[#4a2282]">
                  "Det er ikke en massage, hvor noget skal fikses."
                </p>
                <p className="font-medium text-base text-[#1f142e]">
                  Det er et møde med kroppen.
                </p>
                <ul className="text-sm text-[#4e3568] space-y-1.5 pt-1">
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle text-[6px] text-[#d9238c]"></i>
                    <span>Et rum til at mærke dig selv.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle text-[6px] text-[#d9238c]"></i>
                    <span>Et rum til at give slip på det, der er klar til at slippe.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fa-solid fa-circle text-[6px] text-[#d9238c]"></i>
                    <span>Et rum til at opleve kroppen som noget, der fortjener omsorg, opmærksomhed og kærlighed.</span>
                  </li>
                </ul>
              </div>

              {/* Behandlingens faser */}
              <div className="pt-4 space-y-5">
                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2">
                    Indledende samtale
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Her er ord ikke nødvendige.
                  </p>
                  <p className="text-sm text-[#4e3568] leading-relaxed mt-2">
                    Det kan være en følelse af at give slip på din rolle, din maske, din forestilling om, hvem du skal være og bare give dig hen i nydelse af dig selv og din krop.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2">
                    Gaven til din krop
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Under healingmassagen kan du opleve dig selv i en tranceagtig tilstand, hvor gamle minder flyver forbi, glemte følelser kommer op til overfladen, og hvor kroppen føles let og summende af liv.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2">
                    Gaven for livet
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Jeg afslutter med en healing, hvor jeg aktiverer dit lyslegeme. Det vil arbejde videre i dig, længe efter den fysiske massage er afsluttet og være noget, du kan bruge i resten af dit liv.
                  </p>
                </div>
              </div>

            </div>

            {/* Right: Accepted Genre Image & Practical Info */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* Photo: Genrebillede af kvinde der modtager blid massage */}
              <div className="rounded-3xl overflow-hidden shadow-lg border border-[#edd5e8] bg-white">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/ibjerget-kropsterapi-session.jpg', { width: 800, quality: 85, fit: 'cover' })}
                  alt="Blid healingmassage med nærvær og ro"
                  className="w-full h-auto object-cover"
                />
                <div className="p-4 bg-[#fcf8fb] text-xs text-[#9b1b66] text-center border-t border-[#edd5e8]">
                  Blid og nærværende massage, hvor hele kroppen mødes uden krav
                </div>
              </div>

              {/* Praktisk information */}
              <div id="booking" className="bg-[#381d6e] text-[#faf5f9] rounded-3xl p-8 border border-[#5f2a99] shadow-lg space-y-6">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#e895c8] block mb-1">
                    Fakta & Booking
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-white">
                    Praktisk information
                  </h3>
                </div>

                <div className="space-y-3.5 text-sm">
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Type:</span>
                    <span className="font-semibold text-white">Massage med olie</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Varighed:</span>
                    <span className="font-semibold text-white">60-75 minutter</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Pris:</span>
                    <span className="font-semibold text-white">500 kr.</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Sted:</span>
                    <span className="font-semibold text-white text-right">Ibjergvej 12, 5792 Årslev</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Afbud:</span>
                    <span className="font-semibold text-white">Senest 24 timer før</span>
                  </div>
                </div>

                {/* Booking til Niels Boye */}
                <div className="pt-4 border-t border-[#5f2a99] space-y-4">
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wider text-[#e895c8] font-semibold">
                      Behandler
                    </p>
                    <p className="text-lg font-serif font-bold text-white">
                      Niels Boye
                    </p>
                    <p className="text-xs text-[#f7eefa]">
                      Kontakt Niels direkte for tidsbestilling og spørgsmål:
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 pt-2">
                    <a
                      href="tel:66157902"
                      className="flex items-center justify-center gap-3 w-full py-3.5 px-5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow transition-colors"
                    >
                      <i className="fa-solid fa-phone" aria-hidden="true"></i>
                      <span>Ring på 66 15 79 02</span>
                    </a>
                    <a
                      href="mailto:niels@ibjerget.dk"
                      className="flex items-center justify-center gap-3 w-full py-3 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors"
                    >
                      <i className="fa-regular fa-envelope" aria-hidden="true"></i>
                      <span>Skriv til niels@ibjerget.dk</span>
                    </a>
                  </div>

                  <div className="p-3 bg-[#4a2282] rounded-xl text-xs text-[#f7eefa] leading-relaxed">
                    <p>
                      <strong>Bemærk:</strong> Niels er Elektro Hyper Sensitiv, og derfor skal mobiltelefon og eventuel smartwatch være slukket under behandlingen.
                    </p>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </div>
      </section>
    </>
  );
}
